import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, IconButton, Tooltip } from '@mui/material';
import DeleteDialog from '../../components/DeleteDialog';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import { getAllExpenses, deleteExpense } from '../../apis/expenseSlice';
import ExpenseDialog from './ExpenseDialog';
import moment from 'moment';
import DoctorStyle from '../doctors/Doctors.module.scss';
import CommonAgGrid from '../../components/CommonAgGrid/CommonAgGrid';

const ExpensePage = () => {
  const dispatch = useDispatch();
  const { expenses, expenseLoading, total, totalCount } = useSelector((state) => state.expenseData);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    callApi();
  }, [page, pageSize]);

  const callApi = () => {
    dispatch(getAllExpenses({ page, pageSize }));
  };

  const handleEdit = (data) => {
    setEditData(data);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    const response = await dispatch(deleteExpense(id));
    if (!response.payload?.error) {
      toast.success('Expense deleted successfully');
      callApi();
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteId === null) return;
    await handleDelete(deleteId);
    setDeleteOpen(false);
    setDeleteId(null);
  };

  const handleCancelDelete = () => {
    setDeleteOpen(false);
    setDeleteId(null);
  };

  const columns = useMemo(
    () => [
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        cellRenderer: (params) => (
          <div>
            <Tooltip title="Edit">
              <IconButton onClick={() => handleEdit(params.data)} color="primary" size="small">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton onClick={() => handleDeleteClick(params.data?._id)} color="error" size="small">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
      {
        field: 'clinicName',
        headerName: 'Clinic Name',
        minWidth: 170,
        valueGetter: (params) => params.data?.clinicId?.name || 'N/A',
      },
      { field: 'description', headerName: 'Description', minWidth: 170 },
      {
        field: 'category',
        headerName: 'Category',
        minWidth: 120,
        valueGetter: (params) => params.data?.category || '',
      },
      {
        field: 'amount',
        headerName: 'Amount',
        minWidth: 110,
        valueGetter: (params) => `₹ ${Number(params.data?.amount || 0).toFixed(2)}`,
      },
      {
        field: 'paymentMode',
        headerName: 'Payment Mode',
        minWidth: 130,
        valueGetter: (params) => params.data?.paymentMode || '',
      },
      {
        field: 'expenseDate',
        headerName: 'Date',
        minWidth: 120,
        valueGetter: (params) => moment(params.data?.expenseDate).format('DD/MM/YYYY'),
      },
    ],
    [],
  );

  return (
    <div>
      <Card className={DoctorStyle.tableCard}>
        <DeleteDialog
          open={deleteOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
        <div className={DoctorStyle.tableHeader}>
          <h2 className={DoctorStyle.tableTitle}>Expenses</h2>
          <Button
            className={DoctorStyle.addBtn}
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditData(null);
              setOpen(true);
            }}
          >
            Add Expense
          </Button>
        </div>
        <CommonAgGrid
          rowData={expenses || []}
          columnDefs={columns}
          loading={expenseLoading}
          getRowId={(e) => String(e.data?._id)}
          height={280}
          pagination
          paginationMode="server"
          rowCount={Number(totalCount ?? total) || 0}
          pageSizeOptions={[5, 10, 25, 50]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
        />
      </Card>

      <ExpenseDialog
        open={open}
        handleClose={() => setOpen(false)}
        editData={editData}
        callApi={callApi}
      />
    </div>
  );
};

export default ExpensePage;
